<script setup lang="ts">
import { computed, toRefs } from 'vue';
import {
  ContentsContext,
  Feature,
  PageName,
  SummaryInfo,
} from '@/api/tracking';
import AdpDocumentActionBar from '@/components/adp-document/actions/adp-document-action-bar.vue';
import DocumentBasicInfoLayout from '@/components/common/adp-document/basic-info/document-basic-info-layout.vue';
import DocumentMediaName from '@/components/common/adp-document/basic-info/document-media-name.vue';
import NewArticleTag from '@/components/common/atoms/new-article-tag.vue';
import RankBadge from '@/components/common/atoms/rank-badge.vue';
import CommentCreateModal from '@/components/modals/comment-create-modal.vue';
import DialogGroupMark from '@/components/modals/dialog-group-mark.vue';
import MarkModal from '@/components/modals/mark-modal.vue';
import {
  AdpDocument,
  AdpDocumentOptionAction,
  AdpDocumentOptionActionName,
  FeedType,
  Group,
  Mention,
} from '@/types';
import type { InsightSourceContext } from '@/types/insights';
import { useAdpDocumentActions } from '@/utils/actions/useAdpDocumentActions';
import {
  articleClickedEvent,
  articleContextMenuClickedEvent,
} from '@/utils/actions/view';
import { getPublisher, isNewArticle } from '@/utils/adpDocument';
import { useAdpDocumentMarkPresentation } from '@/utils/composables/useAdpDocumentMarkPresentation';
import { useAdpDocumentModals } from '@/utils/composables/useAdpDocumentModals';
import {
  copyLinkOptionAction,
  useCopyDocumentLink,
} from '@/utils/composables/useCopyDocumentLink';
import { useCurrentPathForTracking } from '@/utils/composables/useCurrentPathForTracking';
import { useLayoutVariant } from '@/utils/composables/useLayoutVariant';
import { useEmitter } from '@/utils/vue';

interface Props {
  adpDocument: AdpDocument;
  rank?: number;
  themeId?: number;
  showRankBadge?: boolean;
  pageName: PageName;
  feature: Feature;
  feedType?: FeedType;
  showPublishedDate?: boolean;
  showOptionMenu?: boolean;
  // 要約起点の閲覧計測に載せる属性。survey など要約から開くカードで渡す
  contentsContext?: ContentsContext;
  summaryInfo?: SummaryInfo;
  insightSourceContext?: InsightSourceContext;
  imageVariant?: 'banner' | 'thumbnail';
  trendCompanyText?: string;
  optionActions?: AdpDocumentOptionAction[];
}

const props = withDefaults(defineProps<Props>(), {
  showRankBadge: false,
  showPublishedDate: false,
  showOptionMenu: false,
  imageVariant: 'thumbnail',
  optionActions: () => [],
});

const emit = defineEmits<{
  optionAction: [emitName: AdpDocumentOptionActionName];
  'article-clicked': [adpDocument: AdpDocument];
}>();

const { getCurrentPathForTracking } = useCurrentPathForTracking();
const layoutVariant = useLayoutVariant();
const emitter = useEmitter();

const { adpDocument } = toRefs(props);
const { copyLink } = useCopyDocumentLink();

const optionActions = computed<AdpDocumentOptionAction[]>(() => [
  copyLinkOptionAction,
  ...props.optionActions,
]);
const handleOptionAction = (emitName: AdpDocumentOptionActionName) => {
  if (emitName === 'copy-link') {
    if (adpDocument.value) copyLink(adpDocument.value);
    return;
  }
  emit('optionAction', emitName);
};

const sendViewProps = computed(() => ({
  adpDocument: props.adpDocument,
  themeId: props.themeId ?? undefined,
  contentsContext: props.contentsContext,
  summary: props.summaryInfo,
  trackingBaseData: {
    pageName: props.pageName,
    pageUrl: getCurrentPathForTracking(),
    feature: props.feature,
    feedType: props.feedType,
    insightSourceContext: props.insightSourceContext,
    layoutVariant,
  },
}));

const handleArticleClick = async () => {
  adpDocument.value.is_read = true;
  await articleClickedEvent(sendViewProps.value, props.adpDocument);
  emitter.emit('article-viewed', props.adpDocument);
};

const optionMenuZIndex = Number(
  getComputedStyle(document.body).getPropertyValue('--z-popover-tooltip'),
);

const { isMarked, markedUsers, markedGroups, commentedUsers } =
  useAdpDocumentMarkPresentation(adpDocument);

const {
  isPostingComment,
  theme,
  markArticle,
  groupMarkArticle,
  commentArticle,
} = useAdpDocumentActions(adpDocument, {
  pageName: props.pageName,
  feature: props.feature,
  themeId: props.themeId,
  contentsContext: props.contentsContext,
  insightSourceContext: props.insightSourceContext,
});

const {
  actionModal,
  showCreateCommentModal,
  showGroupMarkModal,
  showMarkModal,
  closeActionModal,
} = useAdpDocumentModals();

// アクション成功時のみモーダルを閉じる。失敗時は例外が伝播して閉じないため、
// ユーザーは同じ内容で再試行できる。
const markAndClose = async (markProps?: { tags?: string[]; memo?: string }) => {
  await markArticle(markProps);
  closeActionModal();
};

const groupMarkAndClose = async (payload: {
  group: Group;
  tags?: string[];
  memo?: string;
}) => {
  await groupMarkArticle(payload);
  closeActionModal();
};

const commentAndClose = async (payload: {
  group: Group;
  content: string;
  mention?: Mention;
}) => {
  await commentArticle(payload);
  closeActionModal();
};

const showNewTag = computed(
  () => !!props.trendCompanyText && isNewArticle(adpDocument.value),
);
</script>

<template>
  <div
    class="adp-doc-card-small"
    :class="{ 'is-banner': imageVariant === 'banner' }"
    :key="adpDocument.id"
  >
    <RankBadge
      class="rank-badge-outer"
      v-if="showRankBadge && rank && rank <= 3 && imageVariant !== 'banner'"
      :rank="rank"
    />
    <DocumentBasicInfoLayout
      :adp-document="adpDocument"
      size="s"
      :image-variant="imageVariant"
      banner-ratio="16to9"
      :show-published-date="showPublishedDate"
      @article-link-clicked="handleArticleClick"
      @article-link-context-menu="
        articleContextMenuClickedEvent(sendViewProps, $event, adpDocument)
      "
    >
      <template #publisher>
        <DocumentMediaName :publisher="getPublisher(adpDocument)" />
      </template>
      <template #dateExtra>
        <NewArticleTag v-if="showNewTag" />
        <slot name="dateExtra" />
      </template>
      <template #boxBottom>
        <div class="trend-company c-text-regular--s" v-if="trendCompanyText">
          {{ trendCompanyText }}
        </div>
      </template>
    </DocumentBasicInfoLayout>
    <AdpDocumentActionBar
      class="actions-row"
      size="s"
      :adp-document="adpDocument"
      :is-marked="isMarked"
      :marked-users="markedUsers"
      :marked-groups="markedGroups"
      :commented-users="commentedUsers"
      :option-actions="optionActions"
      :show-option-menu="showOptionMenu"
      :zindex="optionMenuZIndex"
      :mark-handler="markArticle"
      @handle-popup-menu-click="showMarkModal"
      @handle-group-mark-click="showGroupMarkModal"
      @handle-comment-click="showCreateCommentModal"
      @option-action="handleOptionAction"
    />
    <MarkModal
      v-if="actionModal === 'mark'"
      :adp-document="adpDocument"
      :theme="theme"
      :page-name="pageName"
      :feature="feature"
      @submit="markAndClose"
      @close="closeActionModal"
    ></MarkModal>
    <DialogGroupMark
      v-else-if="actionModal === 'group_mark'"
      :adp-document="adpDocument"
      :theme="theme"
      :page-name="pageName"
      :feature="feature"
      @submit="groupMarkAndClose"
      @close="closeActionModal"
    ></DialogGroupMark>
    <CommentCreateModal
      v-else-if="actionModal === 'comment'"
      :adp-document="adpDocument"
      :theme="theme"
      :page-name="pageName"
      :feature="feature"
      :is-posting-comment="isPostingComment"
      @submit="commentAndClose"
      @close="closeActionModal"
    ></CommentCreateModal>
  </div>
</template>

<style scoped lang="scss">
.adp-doc-card-small {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;

  &.is-banner {
    --document-inset: 16px;

    .actions-row {
      padding: 4px var(--document-inset) 12px;
    }
  }

  .rank-badge-outer {
    position: absolute;
    top: -8px;
    left: -16px;
  }

  .trend-company {
    margin-top: 4px;
    color: $color-gray800;
  }

  .actions-row {
    padding-top: 8px;
    margin-top: auto;
  }
}
</style>
